import { apiClient } from './client';

// ── 交易展示 ──

export interface TradeWindow {
  window_index: number;
  trade_date: string | null;
  window_name: string | null;
  price: number;
  volume: number;
}

export interface TradeDay {
  target_date: string;
  windows: TradeWindow[];
  actual_price: number | null;
}

export interface TradeDisplayResponse {
  month: string;
  days: TradeDay[];
}

export async function fetchTradeDisplay(month: string): Promise<TradeDisplayResponse> {
  const res = await apiClient.get('/medium-term-backtest/display', { params: { month } });
  return res.data.data;
}

export async function fetchAvailableMonths(): Promise<string[]> {
  const res = await apiClient.get('/medium-term-backtest/months');
  return res.data.data.months;
}

export async function fetchBacktestReadyMonths(): Promise<string[]> {
  const res = await apiClient.get('/medium-term-backtest/ready-months');
  return res.data.data.months;
}

export async function uploadBacktestExcel(file: File): Promise<{ trade_month: string; trade_rows: number }> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiClient.post('/medium-term-backtest/upload', form);
  return res.data.data;
}

// ── 回测 ──

export interface WindowConfig {
  window_index?: number;
  trade_day: number;
  start_day: number;
  end_day: number;
  name?: string;
}

export interface PhaseConfig {
  start: number;
  end: number;
  ratio: number;
}

export interface StrategyParams {
  mode: 'buyer' | 'seller';
  signal: 'momentum' | 'reversal';
  redline: number;
  flat_benchmark_ratio: number;
  entry_threshold: number;
  L: number;
  x0: number;
  k: number;
  max_pos_limit: number;
  phases: PhaseConfig[];
}

export interface BacktestSummary {
  mode: string;
  avg_bench_pnl: number;
  avg_active_pnl: number;
  alpha_pnl: number;
  alpha_per_mwh: number;
  avg_global_pos: number;
  max_global_pos?: number;
  redline: number | null;
  flat_benchmark_ratio?: number | null;
  total_traded_volume?: number;
  pnl_per_traded_mwh?: number;
  compliance_fill_volume?: number;
  compliance_fill_cost?: number;
  macro_win_rate: number;
  pl_ratio: number;
  sortino_ratio: number | null;
  avg_mdd: number;
  cvar_95: number;
  bench_redline?: number;
  bench_flat?: number;
  bench_zero?: number;
}

export interface DailyAttribution {
  Target_Date: number;
  avg_daily_pnl: number;
  cumulative_pnl: number;
}

export interface PredictionDistribution {
  Target_Date: number;
  q10: number;
  q25: number;
  median: number;
  q75: number;
  q90: number;
  mean: number;
}

export interface TradeWindowDetail {
  window: number;
  avg_buy: number;
  avg_sell: number;
  exec_price: number;
  pos_after: number;
  is_compliance?: boolean;
  source_window_index?: number;
  source_trade_date?: string;
}
export interface TradeDetail {
  Target_Date: number;
  windows: TradeWindowDetail[];
}

export interface PositionCurve {
  Target_Date: number;
  Active_Pos: number;
  Actual_Price: number;
  Active_Cost?: number;
  P_Monthly?: number;
}

export interface BenchmarkSim {
  sim_id: number;
  total_pnl: number;
  position_curve: PositionCurve[];
}
export interface BacktestResult {
  trade_month: string;
  n_simulations: number;
  summary: BacktestSummary;
  daily_attribution: DailyAttribution[];
  prediction_distribution: PredictionDistribution[];
  position_curve: PositionCurve[];
  trade_details?: TradeDetail[];
  benchmark?: BenchmarkSim;
}

export interface BacktestRunRequest {
  trade_month: string;
  windows: WindowConfig[];
  strategy: StrategyParams;
}

export async function runBacktest(req: BacktestRunRequest): Promise<BacktestResult> {
  const res = await apiClient.post('/medium-term-backtest/run', req, { timeout: 600000 });
  return res.data.data;
}

// ── 整月双策略比较 ──

export type BacktestRole = 'buyer' | 'seller';
export type ComparisonStrategy = 'MOMENTUM' | 'REVERSAL';
export type ComparisonRunStatus = 'COMPLETED' | 'INFEASIBLE';
export type TradeZone = 'ACTIVE_NEAR' | 'COMPLIANCE_FAR' | 'BASELINE';
export type TradeIntent = 'INITIAL_POSITION' | 'BENCHMARK' | 'SIGNAL' | 'COMPLIANCE' | 'ORACLE';

export interface BacktestComparisonRequest {
  trade_month: string;
  role: BacktestRole;
  analysis_mode: 'SINGLE_RUN';
  entry_threshold: number;
  half_response_spread: number;
  max_adjustment_ratio: number;
  buyer_lower_bound?: number;
  buyer_upper_bound?: number;
  buyer_bounds_confirmed: boolean;
  seller_flat_ratio: number;
}

export interface ComparisonDailyResult {
  target_date: string;
  pnl: number;
  position_ratio: number;
  position_mwh: number;
  position_average_price: number | null;
  realized_pnl: number;
  terminal_pnl: number;
  price_edge_to_spot: number | null;
  baseline_price: number;
  spot_price: number;
}

export interface ComparisonEvent {
  event_id: string;
  sequence_no: number;
  portfolio_type: string;
  window_index: number;
  trade_date: string | null;
  target_date: string;
  trade_zone: TradeZone;
  intent: TradeIntent;
  signal_type: ComparisonStrategy | null;
  trigger_reason: string;
  action: 'BUY_OPEN' | 'SELL_REDUCE' | 'SELL_OPEN' | 'BUY_COVER';
  execution_price: number;
  requested_quantity_mwh: number;
  filled_quantity_mwh: number;
  unfilled_quantity_mwh: number;
  target_position_before: number;
  target_position_after: number;
  cost_basis_before: number | null;
  cost_basis_after: number | null;
  realized_pnl_delta: number;
  baseline_price: number;
  spot_price: number;
  spread: number | null;
  lead_days: number | null;
  constraint_reasons: string[];
  oracle_edge?: number | null;
}

export interface ComparisonPathResult {
  portfolio_type: string;
  run_status: ComparisonRunStatus;
  infeasible_reason: string | null;
  cash_flow: number;
  terminal_settlement: number;
  total_pnl: number;
  monthly_ratio: number;
  monthly_position_mwh: number;
  signal_traded_volume_mwh: number;
  total_traded_volume_mwh: number;
  final_positions: Record<string, number>;
  daily_results: ComparisonDailyResult[];
  events: ComparisonEvent[];
  snapshots: Array<Record<string, unknown>>;
  excess_pnl_vs_zero?: number;
  excess_pnl_vs_flat?: number;
  unit_gain_vs_zero_yuan_per_mwh?: number;
  unit_gain_vs_flat_yuan_per_mwh?: number;
  execution_efficiency_vs_zero_yuan_per_mwh?: number | null;
  execution_efficiency_vs_flat_yuan_per_mwh?: number | null;
  position_ratio?: number;
}

export interface OracleComparisonResult extends ComparisonPathResult {
  oracle_reference_id: string;
  oracle_logic_version: string;
}

export interface CoverageSummary {
  expected_count: number;
  executable_count: number;
  missing_price_count: number;
  coverage_rate: number | null;
}

export interface WindowCoverageSummary extends CoverageSummary {
  window_index: number;
  execution_order: number;
}

export interface AvailabilityRecord {
  window_index: number;
  execution_order: number;
  trade_date: string;
  target_date: string;
  lead_days: number;
  trade_zone: Exclude<TradeZone, 'BASELINE'>;
  availability_status: 'EXECUTABLE' | 'NO_REPORTED_PRICE';
  execution_price: number | null;
  source_value_state: 'PRESENT' | 'BLANK';
  source_reference: string;
}

export interface BacktestComparisonResult {
  trade_month: string;
  role: BacktestRole;
  analysis_mode: 'SINGLE_RUN';
  evidence_level: 'EXPLORATORY' | 'OUT_OF_SAMPLE';
  conclusion_eligible: boolean;
  run_status: ComparisonRunStatus;
  infeasible_reason: string | null;
  normalized_monthly_energy_mwh: number;
  target_base_energy_mwh: number;
  target_position_limit: number;
  strategy_lock_mode: 'FULL_MONTH';
  comparison_leader: ComparisonStrategy | null;
  parameters: {
    entry_threshold: number;
    half_response_spread: number;
    max_adjustment_ratio: number;
    buyer_lower_bound: number | null;
    buyer_upper_bound: number | null;
    seller_flat_ratio: number;
  };
  strategies: Record<ComparisonStrategy, ComparisonPathResult>;
  benchmarks: Record<string, ComparisonPathResult>;
  oracle: OracleComparisonResult;
  execution_assumption: 'REPORTED_PRICE_FULL_FILL_WITH_HARD_CONSTRAINTS';
  availability_source_semantics: 'BLANK_MEANS_NO_REPORTED_PRICE';
  availability_summary: {
    overall: CoverageSummary;
    ACTIVE_NEAR: CoverageSummary;
    COMPLIANCE_FAR: CoverageSummary;
    by_window: WindowCoverageSummary[];
  };
  availability_records: AvailabilityRecord[];
  event_ledger: ComparisonEvent[];
  snapshot_ledger: Array<Record<string, unknown>>;
  spot_price_method: 'PROVINCE_MONTHLY_WEIGHTED_HOURLY_PRICE';
  settlement_weight_source: 'MEDIUM_TERM_MONTHLY_UPLOAD';
  settlement_weight_month: string;
  settlement_weight_sum: number;
  settlement_weight_values: Array<{ hour: number; weight: number }>;
  settlement_weight_source_reference: string;
}

export async function runBacktestComparison(
  request: BacktestComparisonRequest,
): Promise<BacktestComparisonResult> {
  const res = await apiClient.post('/medium-term-backtest/compare', request, { timeout: 600000 });
  return res.data.data;
}

export async function fetchDerivedWindows(month: string, predictionDays = 10): Promise<WindowConfig[]> {
  const res = await apiClient.get('/medium-term-backtest/windows', {
    params: { month, prediction_days: predictionDays },
  });
  return res.data.data.windows;
}

export interface BacktestRun {
  id: number;
  trade_month: string;
  params: Record<string, unknown>;
  summary: BacktestSummary;
  created_at: string;
}

export async function fetchBacktestRuns(month?: string): Promise<BacktestRun[]> {
  const res = await apiClient.get('/medium-term-backtest/runs', { params: { month } });
  return res.data.data.runs;
}

export interface ExportReportRequest {
  trade_month: string;
  strategy: StrategyParams;
  summary: BacktestSummary;
  daily_attribution: DailyAttribution[];
  position_curve: PositionCurve[];
}

export interface ExportReportResult {
  report: string;
  title: string;
  feishu_sent: boolean;
}

export async function exportReport(req: ExportReportRequest): Promise<ExportReportResult> {
  const res = await apiClient.post('/medium-term-backtest/export-report', req, { timeout: 180000 });
  return res.data.data;
}
